/**
 * src/pages/ResetPassword.jsx — spends an emailed reset token on a new password.
 *
 * Public, no session: the token in the URL is the only credential, like
 * JoinInvite.jsx. There is no "check the token first" request — an invalid,
 * expired or used link is only discovered on submit, and the API gives one
 * generic message for all three (see api/src/routes/auth.js), shown as-is.
 *
 * On success it sends the user to /login with a notice rather than signing
 * them in: the API deliberately doesn't turn a reset link into a session.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../lib/api.js';
import { useNoindex } from '../lib/useNoindex.js';
import { ErrorBanner } from '../components/ui.jsx';

export default function ResetPassword() {
  useNoindex();

  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been changed. Sign in with your new password.' },
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="public-page" style={{ maxWidth: 420 }}>
      <div className="public-header">
        <img src="/favicon.svg" alt="" width="40" height="40" />
        <h1>Choose a new password</h1>
        <p className="muted small">This signs you out on every other device.</p>
      </div>

      <form className="card stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>
        <div>
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label htmlFor="confirm">Confirm new password</label>
          <input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Change password'}
        </button>
        <Link className="btn" to="/forgot-password">
          Send a new link
        </Link>
      </form>
    </div>
  );
}
